import { Css, Palette, useTestIds } from "@homebound/beam";
import { Serie } from "@nivo/line";
import {
  addDays,
  addMonths,
  addYears,
  endOfMonth,
  endOfQuarter,
  endOfYear,
  startOfMonth,
  startOfQuarter,
  startOfToday,
  startOfYear,
} from "date-fns";
import { useMemo } from "react";
import { LineChart } from "src/components/charts/LineChart";
import { getNumberTicks } from "src/components/charts/utils";
import { DirectMarginTab_ChartDataFragment } from "src/routes/projects/budget/components/reports/DirectMarginTab.generated";
import { zeroTo } from "src/utils";
import { DateOnly } from "src/utils/dates";
import { BudgetMetricsViewType } from "../utils";

type DirectMarginOverTimeWidgetProps = {
  viewType: BudgetMetricsViewType;
  data: DirectMarginTab_ChartDataFragment;
};

export function DirectMarginOverTimeWidget({ data, viewType }: DirectMarginOverTimeWidgetProps) {
  const tid = useTestIds({});
  const chartData = useMemo(() => mapToLineChartData(data, viewType), [data, viewType]);
  // Get axis ticks from data
  const { yTicks, xTicks } = useMemo(
    () =>
      getChartTicks(
        chartData.flatMap((p) => p.data).filter((d) => d.y !== null),
        viewType,
      ),
    [chartData, viewType],
  );
  // Determine if the chart data spans multiple years
  const isMultiYearData = useMemo(() => {
    const points = chartData.flatMap((p) => p.data);
    return points.length > 2 && new Date(points.first!.x).getFullYear() !== new Date(points.last!.x).getFullYear();
  }, [chartData]);

  return (
    <div css={Css.p3.bgWhite.br8.hPx(311).ba.bcGray300.$}>
      <div css={Css.gray800.md.mb3.$}>Direct Margin Over Time</div>
      <div css={Css.h("80%").$} {...tid.LineChart}>
        <LineChart
          dataset={chartData.isEmpty ? generateEmptyDataset(0, 30, viewType) : chartData}
          colors={[chartData.isEmpty ? Palette.Transparent : Palette.Blue400]}
          loadingColor={[Palette.Transparent]}
          disabled={chartData.isEmpty}
          emptyStateTooltipText={chartData.isEmpty ? "There are no projects that have started yet." : undefined}
          tooltipValueFormat={(points) => `${(points.first!.data.y as number).toFixed(2)}%`}
          margin={{ top: 5, right: 20, bottom: 30, left: 55 }}
          axisLeft={{
            tickValues: yTicks,
            tickPadding: 20,
            tickSize: 0,
            format: (value: number) => `${+value.toFixed(1)}%`,
          }}
          axisBottom={{
            tickSize: 0,
            tickPadding: 15,
            tickValues: xTicks,
            format:
              viewType === BudgetMetricsViewType.MTD || viewType === BudgetMetricsViewType.QTD
                ? "%b %d"
                : isMultiYearData
                  ? "%b '%y"
                  : "%b",
          }}
          enableSlices="x"
          yScale={{ type: "linear", min: "auto", max: "auto" }}
          gridYValues={yTicks}
          xScale={{ type: "time", format: "%Y-%m-%d", precision: "day" }}
          xFormat="time:%b %Y"
          theme={{ axis: { ticks: { text: { fontSize: 12, fill: Palette.Gray700 } } } }}
        />
      </div>
    </div>
  );
}

function mapToLineChartData({ data }: DirectMarginTab_ChartDataFragment, viewType: BudgetMetricsViewType) {
  const points = data.map(({ date, directMarginInBasisPoints }) => ({
    x: date,
    y: (directMarginInBasisPoints ?? 0) / 100,
  }));
  // Add a null datapoint at the end of the time range to ensure the chart uses full width
  const today = new DateOnly(startOfToday());
  const endDate: DateOnly = (() => {
    switch (viewType) {
      case BudgetMetricsViewType.MTD:
        return new DateOnly(endOfMonth(today));
      case BudgetMetricsViewType.QTD:
        return new DateOnly(endOfQuarter(today));
      case BudgetMetricsViewType.YTD:
        return new DateOnly(endOfYear(today));
      default:
        return today;
    }
  })();
  return points.nonEmpty ? [{ id: "Direct Margin Over Time", data: [...points, { x: endDate, y: null }] }] : [];
}

function getChartTicks(
  data: { x: DateOnly; y: number }[],
  viewType: BudgetMetricsViewType,
): {
  xTicks: Date[] | undefined;
  yTicks: number[];
} {
  // Get the number of ticks based on the view type
  return data.isEmpty
    ? { xTicks: undefined, yTicks: [0, 7.5, 15, 22.5, 30] }
    : {
        xTicks: getFixedDateTicks(
          data.map((d) => d.x),
          viewType,
        ),
        yTicks: getNumberTicks(
          data.map((d) => d.y),
          5,
        ),
      };
}

function getFixedDateTicks(data: Date[], view: BudgetMetricsViewType): Date[] {
  const today = new Date(startOfToday());
  const yearToDateTicks = zeroTo(12).map((i) => addMonths(startOfYear(today), i));

  function generateDateRange(start: Date, end: Date, dateFn: (d: Date) => Date): Date[] {
    const ticks: Date[] = [];
    let date = start;
    while (date <= end) {
      ticks.push(date);
      date = dateFn(date);
    }
    return ticks;
  }

  // Return ticks based on the view type
  switch (view) {
    case BudgetMetricsViewType.YTD: {
      // Tick at the start of each month from Jan to Dec
      return yearToDateTicks;
    }

    case BudgetMetricsViewType.QTD: {
      const startDate = new Date(startOfQuarter(today));
      const endDate = new Date(endOfQuarter(today));
      // Ticks for each Monday in the quarter
      const ticks: Date[] = [];
      let date = new Date(startDate);
      while (date <= endDate) {
        if (date.getDay() === 1) {
          ticks.push(date);
        }
        date = addDays(date, 1);
      }
      return ticks;
    }

    case BudgetMetricsViewType.MTD: {
      // Ticks every 3 days of current month
      return generateDateRange(startOfMonth(today), endOfMonth(today), (d) => addDays(d, 3));
    }

    default: {
      const startDate = data.min();
      const endDate = data.max();
      // Treat like YTD until we get more than a year of data
      if (startDate.getFullYear() === endDate.getFullYear()) {
        return yearToDateTicks;
      }
      // Return monthly ticks
      return generateDateRange(startDate, endDate, (d) => addMonths(d, 1));
    }
  }
}

function generateEmptyDataset(yMin: number, yMax: number, viewType: BudgetMetricsViewType): Serie[] {
  const today = startOfToday();
  const dateRange: Record<BudgetMetricsViewType, [Date, Date]> = {
    [BudgetMetricsViewType.MTD]: [startOfMonth(today), endOfMonth(today)],
    [BudgetMetricsViewType.QTD]: [startOfQuarter(today), addDays(endOfQuarter(today), 1)],
    [BudgetMetricsViewType.YTD]: [endOfMonth(startOfYear(today)), addDays(endOfYear(today), 5)],
    [BudgetMetricsViewType.ALL_TIME]: [addYears(today, -1), today],
  };

  const [start, end] = dateRange[viewType];
  return [
    {
      id: "empty_dataset",
      data: [new DateOnly(start), new DateOnly(end)].map((x, i) => ({ x, y: i === 0 ? yMin : yMax })),
    },
  ];
}
